import { createAsyncThunk } from '@reduxjs/toolkit';
import { itemSemUrlDocumento } from '../utils/documentoNotificacao';

import type {
  AjusteLoteGrupo,
  CorrecaoContato,
  ReenviarAjusteResult,
} from '../types/ajustes.types';

import type {
  ContatoData,
  EnderecoData,
  EnvioDigitalSnapshotOutput,
  ItemEditData,
  ItemFilters,
  ItemUpdateInput,
  ItemUpdateResult,
  ItemWithRelations,
  LoteCustos,
  LoteFilters,
  LoteOutput,
  LoteStats,
  LoteTabCounts,
  ManifestacaoChatResponse,
  ManifestacaoMensagem,
  NotificacaoStats,
  NotificacaoFilters,
  NotificacaoListItem,
  NotificationResponseOptionInput,
  SendDigitalInput,
  SendFisicoInput,
  SendMethod,
  StartNegociacaoBulkResult,
  TemplateItem,
  ValidationErrorSample,
  ValoresEfetivos,
  GeneralDashboardResponse,
  ChannelDashboardResponse,
  ManifestationDashboardResponse,
  BatchDashboardResponse,
  DashboardFilters,
} from '@/features/extrajudicial/types';
import type { PaginatedResponse } from '@/features/extrajudicial/types/common.types';
import type {
  ConsultaDasMensagensAnteriores,
  ContagemDosCamposForaDoContrato,
  FalhaDaRespostaSimples,
  MensagemForaDoContrato,
  MotivoDaFalhaDoChat,
} from '@/features/extrajudicial/types/manifestacaoChatUi.types';
import type {
  LoteConfigInput,
  LoteDetailResponse,
  LoteDraftOutput,
  LoteListResponse,
  LotePerformanceDetailedResponse,
  LotesRascunhoFilters,
  LotesRascunhoResponse,
  MapeamentoColunas,
  RecomendacaoIA,
} from '@/features/extrajudicial/types/lote.types';
import type { AggregatedIssue as PreValidationIssue } from '@/shared/components/ui/common';
import type { EnderecoDoCep } from '@/shared/types/cep';
import type { CorrecaoNomesLoteResult } from '@/shared/types/correcaoNomes';
import { lookupCep } from '@/shared/utils/cep';
import { getErrorMessage } from '@/shared/utils/error';
import { isRecord } from '@/shared/utils/apiResponse';
import { registrarAcao } from '@/shared/utils/reportAction';
import { reportError } from '@/shared/utils/reportError';
import { downloadBlob } from '@/shared/utils/requestBuilder';
import {
  extrajudicialService,
  FalhaDaConsultaDaManifestacao,
  ManifestacaoForaDoContrato,
  MENSAGEM_DE_FALHA_DA_MANIFESTACAO,
} from './services';
import { RootState } from '@/store';
import { buildNotificationCsv } from '@/features/negociacoes/utils';

export const baixarModeloCsv = createAsyncThunk<
  void,
  {
    sendMethod: SendMethod;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
  },
  { rejectValue: string }
>(
  'extrajudicial/baixarModeloCsv',
  async (
    { sendMethod, whatsappTemplateId, emailTemplateId, fisicoTemplateId },
    { rejectWithValue },
  ) => {
    try {
      await extrajudicialService.baixarModeloCsv({
        sendMethod,
        whatsappTemplateId,
        emailTemplateId,
        fisicoTemplateId,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const criarLote = createAsyncThunk<
  { loteId: string },
  {
    file: File;
    sendMethod: SendMethod;
    batchName: string;
    batchCode?: string;
    loteId?: string;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    prazoDias?: number;
    ignoreRows?: number[];
  },
  {
    rejectValue: {
      message: string;
      errorsSample?: ValidationErrorSample[];
      code?: string;
      issues?: PreValidationIssue[];
      hasCritical?: boolean;
      totalRows?: number;
    };
  }
>('extrajudicial/criarLote', async (payload, { rejectWithValue }) => {
  try {
    return await extrajudicialService.criarLote(payload);
  } catch (error) {
    const e = error as Error & {
      errorsSample?: ValidationErrorSample[];
      code?: string;
      issues?: PreValidationIssue[];
      hasCritical?: boolean;
      totalRows?: number;
    };
    return rejectWithValue({
      message: e.message,
      errorsSample: e.errorsSample,
      code: e.code,
      issues: e.issues,
      hasCritical: e.hasCritical,
      totalRows: e.totalRows,
    });
  }
});

export const criarIndividual = createAsyncThunk<
  { loteId: string; itemId: string; numeroNotificacao: string | null },
  {
    sendMethod: SendMethod;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    breveRelato: string;
    valor: number;
    contato?: ContatoData;
    endereco?: EnderecoData;
    metadata?: Record<string, string>;
    margemNegociacao?: number;
    requerentePessoaId?: string;
    requeridoPessoaId?: string;
    prazoDias?: number;
    responseOptions?: NotificationResponseOptionInput[];
  },
  {
    rejectValue: {
      message: string;
      errorsSample?: ValidationErrorSample[];
    };
  }
>('extrajudicial/criarIndividual', async (payload, { rejectWithValue }) => {
  try {
    return await extrajudicialService.criarIndividual(payload);
  } catch (error) {
    const e = error as Error & { errorsSample?: ValidationErrorSample[] };
    return rejectWithValue({ message: e.message, errorsSample: e.errorsSample });
  }
});

export const uploadNotificacaoAnexos = createAsyncThunk<
  void,
  { itemId: string; files: File[] },
  { rejectValue: string }
>('extrajudicial/uploadNotificacaoAnexos', async ({ itemId, files }, { rejectWithValue }) => {
  try {
    await extrajudicialService.uploadNotificacaoAnexos(itemId, files);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao enviar anexos';
    return rejectWithValue(message);
  }
});

export const criarLoteComMapeamento = createAsyncThunk<
  { loteId: string },
  {
    file: File;
    sendMethod: SendMethod;
    batchName: string;
    batchCode?: string;
    loteId?: string;
    mapeamento: MapeamentoColunas;
    whatsappTemplateId?: string;
    emailTemplateId?: string;
    fisicoTemplateId?: string;
    prazoDias?: number;
    ignoreRows?: number[];
  },
  {
    rejectValue: {
      message: string;
      errorsSample?: ValidationErrorSample[];
      code?: string;
      issues?: PreValidationIssue[];
      hasCritical?: boolean;
      totalRows?: number;
    };
  }
>('extrajudicial/criarLoteComMapeamento', async (payload, { rejectWithValue }) => {
  try {
    return await extrajudicialService.criarLoteComMapeamento(payload);
  } catch (error) {
    const e = error as Error & {
      errorsSample?: ValidationErrorSample[];
      code?: string;
      issues?: PreValidationIssue[];
      hasCritical?: boolean;
      totalRows?: number;
    };
    return rejectWithValue({
      message: e.message,
      errorsSample: e.errorsSample,
      code: e.code,
      issues: e.issues,
      hasCritical: e.hasCritical,
      totalRows: e.totalRows,
    });
  }
});

export const logUploadView = createAsyncThunk<void, string, { rejectValue: string }>(
  'extrajudicial/logUploadView',
  async (loteId, { rejectWithValue }) => {
    try {
      await extrajudicialService.logUploadView(loteId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const criarLoteRascunho = createAsyncThunk<LoteDraftOutput, void, { rejectValue: string }>(
  'extrajudicial/criarLoteRascunho',
  async (_, { rejectWithValue }) => {
    try {
      return await extrajudicialService.criarLoteRascunho();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const corrigirNomesLote = createAsyncThunk<
  CorrecaoNomesLoteResult,
  string,
  { rejectValue: string }
>('extrajudicial/corrigirNomesLote', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.corrigirNomesEmLote(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const atualizarConfiguracaoLote = createAsyncThunk<
  LoteOutput,
  { loteId: string; data: LoteConfigInput },
  { rejectValue: string }
>('extrajudicial/atualizarConfiguracaoLote', async ({ loteId, data }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.atualizarConfiguracaoLote(loteId, data);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const avancarConfiguracaoLote = createAsyncThunk<
  LoteOutput,
  {
    loteId: string;
    data: {
      batchName: string;
      sendMethod: string;
      whatsappTemplateId?: string;
      emailTemplateId?: string;
      fisicoTemplateId?: string;
    };
  },
  { rejectValue: string }
>('extrajudicial/avancarConfiguracaoLote', async ({ loteId, data }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.avancarConfiguracaoLote(loteId, data);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const cancelarConfiguracaoLote = createAsyncThunk<void, string, { rejectValue: string }>(
  'extrajudicial/cancelarConfiguracaoLote',
  async (loteId, { rejectWithValue }) => {
    try {
      await extrajudicialService.cancelarConfiguracaoLote(loteId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterMetodosPermitidos = createAsyncThunk<string[], void, { rejectValue: string }>(
  'extrajudicial/obterMetodosPermitidos',
  async (_, { rejectWithValue }) => {
    try {
      return await extrajudicialService.obterMetodosPermitidos();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterUltimoLoteRascunho = createAsyncThunk<
  LoteOutput | null,
  void,
  { rejectValue: string }
>('extrajudicial/obterUltimoLoteRascunho', async (_, { rejectWithValue }) => {
  try {
    const { lotes } = await extrajudicialService.listarLotes({
      emRascunho: true,
      page: 1,
      pageSize: 1,
      sortBy: 'dataCriacao',
      sortOrder: 'desc',
    });

    return lotes[0] ?? null;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const listarLotes = createAsyncThunk<LoteListResponse, LoteFilters, { rejectValue: string }>(
  'extrajudicial/listarLotes',
  async (filters, { rejectWithValue }) => {
    try {
      return await extrajudicialService.listarLotes(filters);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

// Uma chamada só, com `emRascunho`: o back decide o que é rascunho (todo lote ainda não
// confirmado) e pagina. Antes eram N chamadas, uma por status, com a página N de cada uma
// concatenada no cliente — e, desde que a listagem passou a esconder lote não confirmado (core
// #1652), todas voltavam vazias.
const buscarLotesEmRascunho = async (
  filters: LotesRascunhoFilters,
): Promise<LotesRascunhoResponse> => {
  const { lotes, pagination } = await extrajudicialService.listarLotes({
    emRascunho: true,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 10,
    sortBy: 'dataAtualizacao',
    sortOrder: 'desc',
  });

  return { lotes, total: pagination.total };
};

export const listarLotesRascunho = createAsyncThunk<
  LotesRascunhoResponse,
  LotesRascunhoFilters,
  { rejectValue: string }
>('extrajudicial/listarLotesRascunho', async (filters, { rejectWithValue }) => {
  try {
    return await buscarLotesEmRascunho(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const contarLotesRascunho = createAsyncThunk<number, void, { rejectValue: string }>(
  'extrajudicial/contarLotesRascunho',
  async (_, { rejectWithValue }) => {
    try {
      const { total } = await buscarLotesEmRascunho({ pageSize: 1 });

      return total;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const exportNotificationCsv = createAsyncThunk<
  void,
  NotificacaoFilters,
  { rejectValue: string; state: RootState }
>('negociacoes/exportNegociacaoCasosCsv', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState();
    const user = state.auth.user;

    if (!user?.empresaId) {
      return rejectWithValue('Não autenticado');
    }

    if (typeof window === 'undefined') {
      return rejectWithValue('Exportação disponível apenas no navegador');
    }

    const limit = 100;

    const firstPage = await extrajudicialService.listarNotificacoes({
      ...filters,
      page: 1,
      pageSize: limit,
    });

    const allItems = [...firstPage.items];

    for (let currentPage = 2; currentPage <= firstPage.totalPages; currentPage += 1) {
      const pageResult = await extrajudicialService.listarNotificacoes({
        ...filters,
        page: currentPage,
        pageSize: limit,
      });

      allItems.push(...pageResult.items);
    }

    const csv = buildNotificationCsv(allItems);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

    const now = new Date();
    const filenameSafeTimestamp = now
      .toISOString()
      .replace('T', '-')
      .replaceAll(':', '-')
      .slice(0, 19);
    const filename = `notificacoes-${filenameSafeTimestamp}.csv`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao exportar CSV');
  }
});

export const obterLote = createAsyncThunk<LoteDetailResponse, string, { rejectValue: string }>(
  'extrajudicial/obterLote',
  async (id, { rejectWithValue }) => {
    try {
      return await extrajudicialService.obterLote(id);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterLotePerformance = createAsyncThunk<
  LotePerformanceDetailedResponse,
  string,
  { rejectValue: string }
>('extrajudicial/obterLotePerformance', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterLotePerformance(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterLoteSummary = createAsyncThunk<
  { lote: LoteOutput },
  string,
  { rejectValue: string }
>('extrajudicial/obterLoteSummary', async (id, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterLoteSummary(id);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterEstatisticas = createAsyncThunk<LoteStats, void, { rejectValue: string }>(
  'extrajudicial/obterEstatisticas',
  async (_, { rejectWithValue }) => {
    try {
      return await extrajudicialService.obterEstatisticas();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const listarItensDoLote = createAsyncThunk<
  PaginatedResponse<ItemWithRelations>,
  { loteId: string; filters: ItemFilters },
  { rejectValue: string }
>('extrajudicial/listarItensDoLote', async ({ loteId, filters }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.listarItensDoLote(loteId, filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const removerItemDaValidacao = createAsyncThunk<
  {
    lote: LoteOutput;
    removedItem: {
      id: string;
    };
  },
  { loteId: string; itemId: string },
  { rejectValue: string }
>('extrajudicial/removerItemDaValidacao', async ({ loteId, itemId }, { rejectWithValue }) => {
  try {
    const result = await extrajudicialService.removerItemDaValidacao(loteId, itemId);
    return { lote: result.lote, removedItem: { id: result.removedItem.id } };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const listarNotificacoes = createAsyncThunk<
  PaginatedResponse<NotificacaoListItem>,
  NotificacaoFilters,
  { rejectValue: string }
>('extrajudicial/listarNotificacoes', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.listarNotificacoes(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const contarNotificacoes = createAsyncThunk<
  number,
  NotificacaoFilters,
  { rejectValue: string }
>('extrajudicial/contarNotificacoes', async (filters, { rejectWithValue }) => {
  try {
    const { total } = await extrajudicialService.listarNotificacoes({
      ...filters,
      page: 1,
      pageSize: 1,
    });
    return total;
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const listarTemplates = createAsyncThunk<TemplateItem[], void, { rejectValue: string }>(
  'extrajudicial/listarTemplates',
  async (_, { rejectWithValue }) => {
    try {
      return await extrajudicialService.listarTemplates();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterNotificacoesStats = createAsyncThunk<
  NotificacaoStats,
  { search?: string },
  { rejectValue: string }
>('extrajudicial/obterNotificacoesStats', async ({ search }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterNotificacoesStats({ search });
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterLoteTabCounts = createAsyncThunk<
  LoteTabCounts,
  { loteId: string; search?: string },
  { rejectValue: string }
>('extrajudicial/obterLoteTabCounts', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterLoteTabCounts(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterItem = createAsyncThunk<ItemWithRelations, string, { rejectValue: string }>(
  'extrajudicial/obterItem',
  async (numero, { rejectWithValue }) => {
    try {
      return itemSemUrlDocumento(await extrajudicialService.obterItem(numero));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterEntregasPorCanal = createAsyncThunk<
  ItemWithRelations,
  string,
  { rejectValue: string }
>('extrajudicial/obterEntregasPorCanal', async (itemId, { rejectWithValue }) => {
  try {
    return itemSemUrlDocumento(await extrajudicialService.obterItemPorId(itemId));
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterItemPorId = createAsyncThunk<ItemWithRelations, string, { rejectValue: string }>(
  'extrajudicial/obterItemPorId',
  async (id, { rejectWithValue }) => {
    try {
      return itemSemUrlDocumento(await extrajudicialService.obterItemPorId(id));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterSnapshotEnvioDigital = createAsyncThunk<
  EnvioDigitalSnapshotOutput,
  string,
  { rejectValue: string }
>('extrajudicial/obterSnapshotEnvioDigital', async (envioId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterSnapshotEnvioDigital(envioId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterItemParaEdicao = createAsyncThunk<ItemEditData, string, { rejectValue: string }>(
  'extrajudicial/obterItemParaEdicao',
  async (itemId, { rejectWithValue }) => {
    try {
      return await extrajudicialService.obterItemParaEdicao(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const enviarDigital = createAsyncThunk<
  { success: boolean; messageId: string },
  { itemId: string; input: SendDigitalInput },
  { rejectValue: string }
>('extrajudicial/enviarDigital', async ({ itemId, input }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.enviarDigital(itemId, input);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const enviarFisico = createAsyncThunk<
  { success: boolean; messageId: string },
  { itemId: string; input: SendFisicoInput },
  { rejectValue: string }
>('extrajudicial/enviarFisico', async ({ itemId, input }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.enviarFisico(itemId, input);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const reenviarItem = createAsyncThunk<{ success: boolean }, string, { rejectValue: string }>(
  'extrajudicial/reenviarItem',
  async (itemId, { rejectWithValue }) => {
    try {
      return await extrajudicialService.reenviarItem(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const listarAjustes = createAsyncThunk<
  AjusteLoteGrupo[],
  string | void,
  { rejectValue: string }
>('extrajudicial/listarAjustes', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.listarAjustes(loteId || undefined);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const reenviarAjuste = createAsyncThunk<
  ReenviarAjusteResult,
  string,
  { rejectValue: string }
>('extrajudicial/reenviarAjuste', async (itemId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.reenviarAjuste(itemId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const corrigirContatosAjuste = createAsyncThunk<
  void,
  { itemId: string; correcoes: CorrecaoContato[] },
  { rejectValue: string }
>('extrajudicial/corrigirContatosAjuste', async ({ itemId, correcoes }, { rejectWithValue }) => {
  try {
    await extrajudicialService.corrigirContatosAjuste(itemId, correcoes);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const ignorarAjuste = createAsyncThunk<void, string, { rejectValue: string }>(
  'extrajudicial/ignorarAjuste',
  async (itemId, { rejectWithValue }) => {
    try {
      await extrajudicialService.ignorarAjuste(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const reverterAjuste = createAsyncThunk<void, string, { rejectValue: string }>(
  'extrajudicial/reverterAjuste',
  async (itemId, { rejectWithValue }) => {
    try {
      await extrajudicialService.reverterAjuste(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const reenviarLote = createAsyncThunk<
  { count: number },
  { loteId: string; input?: { apenasComFalha?: boolean; itemIds?: string[] } },
  { rejectValue: string }
>('extrajudicial/reenviarLote', async ({ loteId, input }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.reenviarLote(loteId, input);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const downloadErrorsCsv = createAsyncThunk<void, string, { rejectValue: string }>(
  'extrajudicial/downloadErrorsCsv',
  async (loteId, { rejectWithValue }) => {
    try {
      await extrajudicialService.downloadErrorsCsv(loteId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const baixarArquivo = createAsyncThunk<
  void,
  { endpoint: string; filename: string },
  { rejectValue: string }
>('extrajudicial/baixarArquivo', async ({ endpoint, filename }, { rejectWithValue }) => {
  try {
    await downloadBlob(endpoint, filename);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const agendarLote = createAsyncThunk<
  { success: boolean; scheduledAt: string; sendAfter: string },
  string,
  { rejectValue: string }
>('extrajudicial/agendarLote', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.agendarLote(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const confirmarEnvio = createAsyncThunk<
  {
    success: boolean;
    message?: string;
    scheduledAt?: string;
    sendAfter?: string;
    custos?: {
      custoDigital: number;
      custoFisico: number;
      custoTotal: number;
      quantidadeDigital: number;
      quantidadeFisico: number;
    };
  },
  {
    loteId: string;
    mode: 'now' | 'schedule';
    scheduledDate?: string;
    scheduledTime?: string;
    recomendacaoAceita?: boolean;
    negociacaoCasoId?: string;
  },
  { rejectValue: string }
>(
  'extrajudicial/confirmarEnvio',
  async (
    { loteId, mode, scheduledDate, scheduledTime, recomendacaoAceita, negociacaoCasoId },
    { rejectWithValue },
  ) => {
    try {
      return await extrajudicialService.confirmarEnvio(loteId, {
        mode,
        scheduledDate,
        scheduledTime,
        recomendacaoAceita,
        negociacaoCasoId,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const validarParaEnvio = createAsyncThunk<
  {
    canConfirm: boolean;
    errors: string[];
    custos?: {
      custoDigital: number;
      custoFisico: number;
      custoTotal: number;
      quantidadeDigital: number;
      quantidadeFisico: number;
    };
  },
  string,
  { rejectValue: string }
>('extrajudicial/validarParaEnvio', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.validarParaEnvio(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const calcularCustos = createAsyncThunk<
  {
    custoDigital: number;
    custoFisico: number;
    custoTotal: number;
    quantidadeDigital: number;
    quantidadeFisico: number;
  },
  string,
  { rejectValue: string }
>('extrajudicial/calcularCustos', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.calcularCustos(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const cancelarLote = createAsyncThunk<
  { success: boolean; message: string; canceledAt?: string },
  { loteId: string; motivo?: string },
  { rejectValue: string }
>('extrajudicial/cancelarLote', async ({ loteId, motivo }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.cancelarLote(loteId, motivo);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const enviarLoteAgora = createAsyncThunk<
  { success: boolean; message: string },
  string,
  { rejectValue: string }
>('extrajudicial/enviarLoteAgora', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.enviarLoteAgora(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterTempoRestanteLote = createAsyncThunk<
  {
    secondsRemaining: number;
    canCancel: boolean;
    canEdit: boolean;
    sendAfter: string | null;
    status: string;
  },
  string,
  { rejectValue: string }
>('extrajudicial/obterTempoRestanteLote', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterTempoRestanteLote(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const cancelarItem = createAsyncThunk<
  { success: boolean; message: string; canceledAt?: string; cancelType?: string },
  { itemId: string; motivo?: string },
  { rejectValue: string }
>('extrajudicial/cancelarItem', async ({ itemId, motivo }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.cancelarItem(itemId, motivo);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const encerrarItem = createAsyncThunk<
  { statusGeral: string },
  { itemId: string; tipo: 'CONCLUIR' | 'CANCELAR'; motivo?: string },
  { rejectValue: string }
>('extrajudicial/encerrarItem', async ({ itemId, tipo, motivo }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.encerrarItem(itemId, tipo, motivo);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const arquivarItem = createAsyncThunk<{ success: boolean }, string, { rejectValue: string }>(
  'extrajudicial/arquivarItem',
  async (itemId, { rejectWithValue }) => {
    try {
      return await extrajudicialService.arquivarItem(itemId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const restaurarItem = createAsyncThunk<
  { success: boolean },
  string,
  { rejectValue: string }
>('extrajudicial/restaurarItem', async (itemId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.restaurarItem(itemId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const abrirNegociacao = createAsyncThunk<
  void,
  { itemId: string; title?: string; flowId?: string; objective?: string },
  { rejectValue: string }
>(
  'extrajudicial/abrirNegociacao',
  async ({ itemId, title, flowId, objective }, { rejectWithValue }) => {
    try {
      await extrajudicialService.abrirNegociacao(itemId, { title, flowId, objective });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const abrirNegociacaoEmLote = createAsyncThunk<
  StartNegociacaoBulkResult,
  { itemIds: string[]; title?: string; flowId?: string },
  { rejectValue: string }
>(
  'extrajudicial/abrirNegociacaoEmLote',
  async ({ itemIds, title, flowId }, { rejectWithValue }) => {
    try {
      return await extrajudicialService.abrirNegociacaoEmLote({ itemIds, title, flowId });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterInfoCancelamentoItem = createAsyncThunk<
  {
    secondsRemaining: number;
    canCancel: boolean;
    canEdit: boolean;
    sendAfter: string | null;
    status: string;
    cancelType?: string;
  },
  string,
  { rejectValue: string }
>('extrajudicial/obterInfoCancelamentoItem', async (itemId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterInfoCancelamentoItem(itemId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterValoresEfetivos = createAsyncThunk<
  ValoresEfetivos,
  void,
  { rejectValue: string }
>('extrajudicial/obterValoresEfetivos', async (_, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterValoresEfetivos();
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const calcularCustosLote = createAsyncThunk<LoteCustos, string, { rejectValue: string }>(
  'extrajudicial/calcularCustosLote',
  async (loteId, { rejectWithValue }) => {
    try {
      return await extrajudicialService.calcularCustosLote(loteId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const obterRecomendacaoIA = createAsyncThunk<
  RecomendacaoIA,
  string,
  { rejectValue: string }
>('extrajudicial/obterRecomendacaoIA', async (loteId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.obterRecomendacaoIA(loteId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const validarCpfCnpj = createAsyncThunk<
  {
    tipo?: 'CPF' | 'CNPJ';
    documento?: string;
    nome: string;
    fonte?: 'database' | 'api';
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    cep?: string;
  },
  string,
  { rejectValue: string }
>('extrajudicial/validarCpfCnpj', async (doc, { rejectWithValue }) => {
  try {
    return await extrajudicialService.validarCpfCnpj(doc);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const consultarCep = createAsyncThunk<EnderecoDoCep, string, { rejectValue: string }>(
  'extrajudicial/consultarCep',
  async (cep, { rejectWithValue }) => {
    try {
      const result = await lookupCep(cep);
      if (!result.valid) return rejectWithValue(result.error);
      return result.endereco;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Erro ao consultar CEP'));
    }
  },
);

export const atualizarItem = createAsyncThunk<
  ItemUpdateResult,
  { itemId: string; input: ItemUpdateInput },
  { rejectValue: string }
>('extrajudicial/atualizarItem', async ({ itemId, input }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.atualizarItem(itemId, input);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const validarDocumentoIndividual = createAsyncThunk<
  {
    valido: boolean;
    erros: string[];
    nomeOficial: string | null;
    pessoaExistente: { id: string; nome: string } | null;
  },
  { cpfCnpj: string; nome: string },
  { rejectValue: string }
>('extrajudicial/validarDocumentoIndividual', async ({ cpfCnpj, nome }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.validarDocumentoIndividual(cpfCnpj, nome);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const arquivarRespostaNotificacao = createAsyncThunk<
  { success: boolean },
  { itemId: string; respostaId: string },
  { rejectValue: string }
>('extrajudicial/arquivarResposta', async ({ itemId, respostaId }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.arquivarRespostaNotificacao(itemId, respostaId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const baixarAnexoResposta = createAsyncThunk<
  Blob,
  { itemId: string; respostaId: string },
  { rejectValue: string }
>('extrajudicial/baixarAnexoResposta', async ({ itemId, respostaId }, { rejectWithValue }) => {
  try {
    return await extrajudicialService.baixarAnexoResposta(itemId, respostaId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const obterManifestacaoAnexoUrl = createAsyncThunk<
  { url: string; name: string },
  { itemId: string; manifestacaoId: string; anexoIndex: number },
  { rejectValue: string }
>(
  'extrajudicial/obterManifestacaoAnexoUrl',
  async ({ itemId, manifestacaoId, anexoIndex }, { rejectWithValue }) => {
    try {
      return await extrajudicialService.obterManifestacaoAnexoUrl(
        itemId,
        manifestacaoId,
        anexoIndex,
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

export const getGeneralDashboard = createAsyncThunk<
  GeneralDashboardResponse,
  DashboardFilters,
  { rejectValue: string }
>('extrajudicial/dashboard/notifications/general', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.getGeneralDashboard(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const getChannelDashboard = createAsyncThunk<
  ChannelDashboardResponse,
  DashboardFilters,
  { rejectValue: string }
>('extrajudicial/dashboard/notifications/channel', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.getChannelDashboard(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const getManifestationDashboard = createAsyncThunk<
  ManifestationDashboardResponse,
  DashboardFilters,
  { rejectValue: string }
>('extrajudicial/dashboard/notifications/manifestation', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.getManifestationDashboard(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const getBatchDashboard = createAsyncThunk<
  BatchDashboardResponse,
  DashboardFilters,
  { rejectValue: string }
>('extrajudicial/dashboard/notifications/batch', async (filters, { rejectWithValue }) => {
  try {
    return await extrajudicialService.getBatchDashboard(filters);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

const ORIGEM_DA_TELEMETRIA_DO_CHAT = 'extrajudicial.manifestacao.chat';

function contarCamposForaDoContrato(
  foraDoContrato: MensagemForaDoContrato[],
): ContagemDosCamposForaDoContrato {
  const contagem: ContagemDosCamposForaDoContrato = { origem: 0, opcaoEscolhida: 0 };
  for (const { campo } of foraDoContrato) contagem[campo] += 1;
  return contagem;
}

/**
 * Um evento por leitura, e não por mensagem: num descompasso de deploy toda mensagem da página
 * vem quebrada, e um evento por campo encheria o RUM com até 2 × pageSize erros a cada página.
 * Só ids e contagens, porque o valor recusado pode ser o contato de alguém.
 */
function registrarMensagensForaDoContrato(
  itemId: string,
  foraDoContrato: MensagemForaDoContrato[],
  page?: number,
): void {
  const [primeira] = foraDoContrato;
  if (!primeira) return;
  reportError(new Error('Campo da mensagem da manifestação fora do contrato'), {
    source: ORIGEM_DA_TELEMETRIA_DO_CHAT,
    itemId,
    ...(page === undefined ? {} : { page }),
    camposForaDoContrato: contarCamposForaDoContrato(foraDoContrato),
    primeiraMensagemId: primeira.mensagemId,
  });
}

function motivoDaFalhaDoChat(erro: unknown, status: number): MotivoDaFalhaDoChat {
  if (erro instanceof ManifestacaoForaDoContrato) return 'contrato';
  return status === 0 ? 'rede' : 'http';
}

/**
 * Registra a falha no RUM e devolve a mensagem para o `rejectWithValue`. Recusas previstas (4xx)
 * vão como ação, e não como erro; a causa original nunca vai ao RUM porque pode conter PII.
 */
function registrarFalhaDoChat(
  itemId: string,
  erro: unknown,
  page: number,
  signal: AbortSignal,
): string {
  if (signal.aborted || (isRecord(erro) && erro.name === 'AbortError')) {
    return MENSAGEM_DE_FALHA_DA_MANIFESTACAO;
  }
  const status = erro instanceof FalhaDaConsultaDaManifestacao ? erro.erro.status : 0;
  const contexto = { source: ORIGEM_DA_TELEMETRIA_DO_CHAT, itemId, page, status };
  if (status >= 400 && status < 500) {
    registrarAcao('extrajudicial.manifestacao.recusada', contexto);
  } else {
    reportError(new Error(MENSAGEM_DE_FALHA_DA_MANIFESTACAO), {
      ...contexto,
      motivo: motivoDaFalhaDoChat(erro, status),
    });
  }
  return MENSAGEM_DE_FALHA_DA_MANIFESTACAO;
}

async function lerPaginaDoChat(
  itemId: string,
  page: number,
  signal: AbortSignal,
): Promise<ManifestacaoChatResponse> {
  const { pagina, foraDoContrato } = await extrajudicialService.obterManifestacaoChat(
    itemId,
    { page },
    signal,
  );
  registrarMensagensForaDoContrato(itemId, foraDoContrato, page);
  return pagina;
}

/**
 * Primeira página, a mais recente. Substitui o chat e reinicia a sequência das anteriores; o
 * `requestId` desta chamada é o do estado, e o reducer descarta resposta que chega atrasada.
 */
export const obterManifestacaoChat = createAsyncThunk<
  ManifestacaoChatResponse,
  string,
  { rejectValue: string }
>('extrajudicial/manifestacao/chat', async (itemId, { rejectWithValue, signal }) => {
  try {
    return await lerPaginaDoChat(itemId, 1, signal);
  } catch (error) {
    return rejectWithValue(registrarFalhaDoChat(itemId, error, 1, signal));
  }
});

/** Página seguinte à mais antiga já carregada; o reducer a põe antes das que já estão na tela. */
export const obterManifestacaoChatAnteriores = createAsyncThunk<
  ManifestacaoChatResponse,
  ConsultaDasMensagensAnteriores,
  { rejectValue: string }
>(
  'extrajudicial/manifestacao/chatAnteriores',
  async ({ itemId, page }, { rejectWithValue, signal }) => {
    try {
      return await lerPaginaDoChat(itemId, page, signal);
    } catch (error) {
      return rejectWithValue(registrarFalhaDoChat(itemId, error, page, signal));
    }
  },
);

/**
 * No envio, `ManifestacaoForaDoContrato` só nasce depois do `response.ok`: o core já gravou a
 * resposta. O POST não é idempotente, então em vez de repeti-lo o chat relê a primeira página, que
 * traz a mensagem e a cota do servidor. A releitura fica dentro do envio para o composer seguir
 * bloqueado até ela terminar, e só acontece se o chat aberto ainda é o do item.
 */
export const enviarRespostaSimples = createAsyncThunk<
  ManifestacaoMensagem,
  { itemId: string; conteudo: string },
  { rejectValue: FalhaDaRespostaSimples; state: RootState }
>(
  'extrajudicial/manifestacao/respostaSimples',
  async ({ itemId, conteudo }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { mensagem, foraDoContrato } = await extrajudicialService.enviarRespostaSimples(
        itemId,
        conteudo,
      );
      registrarMensagensForaDoContrato(itemId, foraDoContrato);
      return mensagem;
    } catch (error) {
      if (!(error instanceof ManifestacaoForaDoContrato)) {
        return rejectWithValue({ motivo: 'envio', mensagem: (error as Error).message });
      }
      reportError(error, { source: ORIGEM_DA_TELEMETRIA_DO_CHAT, itemId });
      if (getState().extrajudicial.manifestacaoChatItemId === itemId) {
        await dispatch(obterManifestacaoChat(itemId));
      }
      return rejectWithValue({ motivo: 'leitura_apos_envio', mensagem: error.message });
    }
  },
);

export const marcarManifestacaoLida = createAsyncThunk<
  { marcadas: number },
  string,
  { rejectValue: string }
>('extrajudicial/manifestacao/marcarLida', async (itemId, { rejectWithValue }) => {
  try {
    return await extrajudicialService.marcarManifestacaoLida(itemId);
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});
